import axios from "axios";
import { useState, useEffect, useCallback } from "react";
import foodImage from '../img/fondoAlmuerzo.jpeg';
import dinnerImage from '../img/fondocena.jpg';
import ConfiguradorEtiquetas from '../Modal/ConfiguradorEtiquetas';
import {
    normalize,
    getPedidoId,
    calculateDateRange,
    exportPedidosToExcel,
    generateZPLFromConfig,
    generateRawZPL,
    generatePDFHTML
} from './AbmPedidosUtils';

const apiUrlPedidos = process.env.REACT_APP_API_PEDIDOS;
const apiUrlAddresses = `${process.env.REACT_APP_API_URL}/addres`; // sedes/direcciones
const apiUrlBebidas = `${process.env.REACT_APP_API_URL}/bebidas`;
const apiUrlEmpresas = `${process.env.REACT_APP_API_URL}/empresas`;

function AbmPedidos() {
    const [pedidos, setPedidos] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresa, setSelectedEmpresa] = useState(() => localStorage.getItem('selectedEmpresaAbmPedidos') || 'all');
    const [todasLasSedes, setTodasLasSedes] = useState([]); // Todas las sedes sin filtrar
    const [sedes, setSedes] = useState([]); // [{ id: string, label: string, direccion: string }] - Filtradas por empresa
    const [bebidas, setBebidas] = useState([]); // [{ id_bebida, tipo, marca, sabor }]
    const [selectedSede, setSelectedSede] = useState(() => localStorage.getItem('selectedSedeAbmPedidos') || 'all'); // 'all' | id_direccion
    const [showConfigurador, setShowConfigurador] = useState(false);
    const [etiquetaConfig, setEtiquetaConfig] = useState(null);
    const [selectedPedidos, setSelectedPedidos] = useState([]);
    const [minDate, setMinDate] = useState('');
    const [maxDate, setMaxDate] = useState('');
    // const [openDropdown, setOpenDropdown] = useState(null); // 'almuerzo' | 'cena' | null
    const [printChooserOpen, setPrintChooserOpen] = useState(false);
    const [printChooserTypes, setPrintChooserTypes] = useState({ almuerzo: true, cena: true });
    const [printChooserScope, setPrintChooserScope] = useState('all'); // 'all' | 'selected'
    const [printMethod, setPrintMethod] = useState('usb'); // 'qz' | 'pdf' | 'ip' | 'backend' | 'raw' | 'usb'
    const [printerIP, setPrinterIP] = useState(''); // Para impresión directa por IP
    const [usbPrinters, setUsbPrinters] = useState([]);
    const [selectedUSBPrinter, setSelectedUSBPrinter] = useState('');
    const [loadingPrinters, setLoadingPrinters] = useState(false);
    // const [systemPrinters, setSystemPrinters] = useState([]);
    const [printDiagnostics, setPrintDiagnostics] = useState({ qzStatus: 'unknown', systemInfo: null });
    const [showDiagnostics, setShowDiagnostics] = useState(false);

    const getPedidos = useCallback(async () => {
        try {
            const token = sessionStorage.getItem('token');
            const res = await axios.get(apiUrlPedidos, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = res.data || [];
            setPedidos(data);
            const { min, max } = calculateDateRange(data);
            setMinDate(min);
            setMaxDate(max);
        } catch (error) {
            console.error('Error al obtener los pedidos:', error);
        }
    }, [selectedSede]);

    useEffect(() => {
        getPedidos();
    }, [getPedidos]);

    // Cargar empresas
    useEffect(() => {
        const loadEmpresas = async () => {
            try {
                const res = await axios.get(apiUrlEmpresas);
                setEmpresas(res.data || []);
            } catch (e) {
                console.error('Error cargando empresas:', e);
            }
        };
        loadEmpresas();
    }, []);

    // Cargar sedes/direcciones para el selector
    useEffect(() => {
        const loadSedes = async () => {
            try {
                const res = await axios.get(apiUrlAddresses);
                const list = (res.data || []).map((s) => ({
                    id: String(s.id_direccion),
                    id_empresa: s.id_empresa,
                    label: s.nombre_sede ? s.nombre_sede : `${s.direccion} - ${s.localidad}`,
                    direccion: s.direccion || '',
                    nombre: s.nombre_sede || '',
                    localidad: s.localidad || ''
                }));
                setTodasLasSedes(list);
                setSedes(list); // Inicialmente todas
            } catch (e) {
                console.error('Error cargando sedes:', e);
            }
        };
        loadSedes();
    }, []);

    // Filtrar sedes cuando cambia la empresa seleccionada
    useEffect(() => {
        if (selectedEmpresa === 'all') {
            setSedes(todasLasSedes);
        } else {
            const sedesFiltradas = todasLasSedes.filter(s => String(s.id_empresa) === String(selectedEmpresa));
            setSedes(sedesFiltradas);
            // Si la sede actual no pertenece a la empresa seleccionada, resetear
            if (selectedSede !== 'all') {
                const sedeValida = sedesFiltradas.find(s => s.id === selectedSede);
                if (!sedeValida) {
                    setSelectedSede('all');
                    localStorage.setItem('selectedSedeAbmPedidos', 'all');
                }
            }
        }
    }, [selectedEmpresa, todasLasSedes, selectedSede]);

    // Cargar bebidas para mapeo en Excel
    useEffect(() => {
        const loadBebidas = async () => {
            try {
                const res = await axios.get(apiUrlBebidas);
                setBebidas(res.data || []);
            } catch (e) {
                console.error('Error cargando bebidas:', e);
            }
        };
        loadBebidas();
    }, []);

    // cargar configuración de etiquetas guardada
    useEffect(() => {
        try {
            const saved = localStorage.getItem('etiquetaConfig');
            if (saved) setEtiquetaConfig(JSON.parse(saved));
        } catch (e) {
            // ignore
        }
    }, []);

    // Filtrado por sede seleccionado (match por 'ubicacion' == direccion de sede)
    const getFilteredPedidos = useCallback(() => {
        if (!selectedSede || selectedSede === 'all') return pedidos;
        const sedeObj = sedes.find(s => s.id === String(selectedSede));
        if (!sedeObj) return [];

        // Si el pedido tiene id_sede, filtrar directamente por id
        const withId = pedidos.filter(p => p && p.id_sede != null);
        const withoutId = pedidos.filter(p => !p || p.id_sede == null);

        const byId = withId.filter(p => String(p.id_sede) === String(selectedSede));

        // Construir posibles tokens para matchear ubicacion (fallback legacy)
        const tokensRaw = [
            sedeObj.direccion,
            sedeObj.nombre,
            sedeObj.label,
            `${sedeObj.direccion} - ${sedeObj.localidad}`,
            sedeObj.localidad
        ].filter(Boolean);
        const tokens = Array.from(new Set(tokensRaw.map(t => normalize(t)).filter(t => t.length > 2)));

        const byUbicacion = withoutId.filter(p => {
            const u = normalize(p?.ubicacion);
            if (!u) return false; // ya no incluimos vacíos; la idea es que todos tengan sede
            return tokens.some(tok => u === tok || u.includes(tok) || tok.includes(u));
        });

        return [...byId, ...byUbicacion];
    }, [pedidos, sedes, selectedSede]);

    // Recalcular rango de fechas cuando cambia el filtro de sede
    useEffect(() => {
        const filtered = getFilteredPedidos();
        const { min, max } = calculateDateRange(filtered);
        setMinDate(min);
        setMaxDate(max);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getFilteredPedidos]);

    // obtener pedidos por tipo (almuerzo / cena) con fallback a 'tipo'
    const getPedidosByType = (type) => {
        const base = getFilteredPedidos();
        let byType = base.filter(p => normalize(p.comida) === type);
        if (base.length > 0 && byType.length === 0) {
            byType = base.filter(p => normalize(p.tipo) === type);
        }
        return byType;
    };

    const isAllSelectedInGroup = (type) => {
        const group = getPedidosByType(type).map(p => getPedidoId(p));
        if (group.length === 0) return false;
        return group.every(id => selectedPedidos.includes(id));
    };

    const handleSelectAllGroup = (type) => {
        const group = getPedidosByType(type).map(p => getPedidoId(p));
        setSelectedPedidos(prev => {
            const allSelected = group.every(id => prev.includes(id));
            if (allSelected) {
                // quitar del conjunto
                return prev.filter(id => !group.includes(id));
            } else {
                // añadir (evitar duplicados)
                const combined = Array.from(new Set([...prev, ...group]));
                return combined;
            }
        });
    };

    const exportToExcel = () => {
        const base = getFilteredPedidos();
        exportPedidosToExcel(base, bebidas, minDate, maxDate);
    };

    const handleSelectPedido = (pedidoId) => {
        const idStr = String(pedidoId);
        setSelectedPedidos(prev => {
            if (prev.includes(idStr)) return prev.filter(id => id !== idStr);
            return [...prev, idStr];
        });
    };

    const handleSelectAll = () => {
        const base = getFilteredPedidos();
        if (selectedPedidos.length === base.length) {
            setSelectedPedidos([]);
        } else {
            setSelectedPedidos(base.map(p => getPedidoId(p)));
        }
    };

    // const selectedCountInGroup = (type) => {
    //     const group = getPedidosByType(type).map(p => getPedidoId(p));
    //     return group.filter(id => selectedPedidos.includes(id)).length;
    // };

    // const buildPedidosForPrint = ({ types = { almuerzo: true, cena: true }, scope = 'all' } = {}) => {
    //     const list = [];
    //     if (scope === 'all') {
    //         if (types.almuerzo) list.push(...getPedidosByType('almuerzo'));
    //         if (types.cena) list.push(...getPedidosByType('cena'));
    //     } else {
    //         // selected scope
    //         const selectedSet = new Set(selectedPedidos);
    //         if (types.almuerzo) list.push(...getPedidosByType('almuerzo').filter(p => selectedSet.has(getPedidoId(p))));
    //         if (types.cena) list.push(...getPedidosByType('cena').filter(p => selectedSet.has(getPedidoId(p))));
    //     }
    //     // dedupe by id
    //     const seen = new Set();
    //     return list.filter(p => {
    //         const id = getPedidoId(p);
    //         if (seen.has(id)) return false;
    //         seen.add(id);
    //         return true;
    //     });
    // };

    // Funciones de diagnóstico de impresoras
    const detectSystemPrinters = async () => {
        try {
            if (!navigator.serviceWorker) {
                return { success: false, error: 'Service Worker no disponible' };
            }

            // Intentar obtener dispositivos de media (puede incluir impresoras virtuales)
            if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const printerDevices = devices.filter(device =>
                    device.kind === 'audiooutput' &&
                    device.label.toLowerCase().includes('printer')
                );
                // setSystemPrinters(printerDevices);
                console.log('Printer devices detected:', printerDevices);
            }

            // Intentar usar la API de impresión del navegador para detectar impresoras
            if (window.navigator.printing) {
                const printers = await window.navigator.printing.getPrinters();
                // setSystemPrinters(printers);
                console.log('System printers detected:', printers);
                return { success: true, printers };
            }

            return { success: false, error: 'API de impresión no disponible' };
        } catch (error) {
            console.error('Error detectando impresoras:', error);
            return { success: false, error: error.message };
        }
    };

    // Función para generar ZPL crudo desde configuración de etiquetas
    // --- Generación de ZPL crudo ---
    // Ya no es necesario definir localmente generateRawZPL ya que se importa de utilidades.
    // Importante: La versión de utilidades requiere (pedidos, config).


    const testQZConnection = async () => {
        try {
            console.log('Testing QZ Tray connection...');
            setPrintDiagnostics(prev => ({ ...prev, qzStatus: 'testing' }));

            // Verificar si QZ está disponible
            if (typeof window.qz === 'undefined') {
                setPrintDiagnostics(prev => ({ ...prev, qzStatus: 'not_loaded' }));
                return { success: false, error: 'QZ Tray no está cargado en el navegador' };
            }

            // Intentar conectar
            if (!window.qz.websocket.isActive()) {
                await window.qz.websocket.connect();
            }

            // Obtener información del sistema
            const version = await window.qz.info.getVersion();
            const printers = await window.qz.printers.find();

            // Buscar impresoras Honeywell específicamente
            const honeywellPrinters = printers.filter(printer =>
                printer.toLowerCase().includes('honeywell') ||
                printer.toLowerCase().includes('pc42') ||
                printer.toLowerCase().includes('pc43') ||
                printer.toLowerCase().includes('pd43') ||
                printer.toLowerCase().includes('intermec')
            );

            const systemInfo = {
                version,
                totalPrinters: printers.length,
                printers,
                honeywellPrinters,
                platform: navigator.platform,
                userAgent: navigator.userAgent
            };

            setPrintDiagnostics(prev => ({
                ...prev,
                qzStatus: 'connected',
                systemInfo
            }));

            return {
                success: true,
                version,
                printers,
                honeywellPrinters,
                systemInfo
            };

        } catch (error) {
            console.error('QZ Tray connection failed:', error);
            setPrintDiagnostics(prev => ({ ...prev, qzStatus: 'failed' }));
            return { success: false, error: error.message };
        }
    };

    const scanForHoneywellIP = async () => {
        const commonIPs = [
            '192.168.1.100', '192.168.1.101', '192.168.1.102',
            '192.168.0.100', '192.168.0.101', '192.168.0.102',
            '10.0.0.100', '10.0.0.101', '10.0.0.102'
        ];

        const results = [];
        for (const ip of commonIPs) {
            try {
                // eslint-disable-next-line no-unused-vars
                const response = await fetch(`http://${ip}:9100`, {
                    method: 'GET',
                    timeout: 2000,
                    mode: 'no-cors'
                });
                results.push({ ip, status: 'responsive' });
            } catch (error) {
                results.push({ ip, status: 'no_response' });
            }
        }

        return results;
    };

    // Ejecutar diagnósticos automáticamente cuando se abre el modal
    useEffect(() => {
        if (printChooserOpen && printDiagnostics.qzStatus === 'unknown') {
            testQZConnection();
            detectSystemPrinters();
        }
        // Cargar impresoras USB cuando se abre el modal
        if (printChooserOpen && usbPrinters.length === 0) {
            loadUSBPrinters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printChooserOpen]);

    // Función para cargar impresoras USB/CUPS
    const loadUSBPrinters = async () => {
        setLoadingPrinters(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/printers`);
            if (response.ok) {
                const data = await response.json();
                setUsbPrinters(data.printers || []);
                // Seleccionar automáticamente Honeywell si existe
                const honeywell = data.printers.find(p =>
                    p.name.toLowerCase().includes('honeywell') && p.isUSB
                );
                if (honeywell) {
                    setSelectedUSBPrinter(honeywell.name);
                }
            }
        } catch (error) {
            console.error('Error cargando impresoras USB:', error);
        } finally {
            setLoadingPrinters(false);
        }
    };

    const handleConfirmPrintAction = async ({ action = 'print' } = {}) => {
        // Validación temprana: verificar que se haya seleccionado al menos un tipo
        if (!printChooserTypes.almuerzo && !printChooserTypes.cena) {
            alert('❌ Seleccioná al menos un tipo (Almuerzo o Cena) para continuar.');
            return;
        }

        // Validación temprana: si el scope es 'selected', debe haber pedidos seleccionados
        if (printChooserScope === 'selected' && (!selectedPedidos || selectedPedidos.length === 0)) {
            alert('❌ No hay pedidos seleccionados.\n\nPor favor:\n• Seleccioná al menos un pedido marcando los checkboxes\n• O cambiá a "Todas" para imprimir todos los pedidos');
            return;
        }

        // Build pedidosToUse by asking the backend so we don't depend on client-side classification
        let pedidosToUse = [];
        let pedidosBeforeSedeFilter = 0;
        try {
            if (printChooserScope === 'selected') {
                // Cuando el scope es 'selected', NO aplicar filtro de sede en la API
                // porque los pedidos ya fueron seleccionados por el usuario
                const res = await axios.get(apiUrlPedidos, { params: { ids: selectedPedidos.join(',') } });
                pedidosToUse = res.data || [];
                console.log(`📥 API retornó ${pedidosToUse.length} pedidos para IDs: ${selectedPedidos.join(',')}`);
            } else {
                // scope === 'all' -> respect types and apply sede filter in API call
                const onlyAlmuerzo = printChooserTypes.almuerzo && !printChooserTypes.cena;
                const onlyCena = printChooserTypes.cena && !printChooserTypes.almuerzo;
                const params = {};

                // Aplicar filtro de sede en la llamada a la API cuando es scope 'all'
                if (selectedSede && selectedSede !== 'all') {
                    params.id_sede = selectedSede;
                }

                if (onlyAlmuerzo) {
                    params.comida = 'almuerzo';
                } else if (onlyCena) {
                    params.comida = 'cena';
                }

                const res = await axios.get(apiUrlPedidos, { params });
                pedidosToUse = res.data || [];
            }
        } catch (err) {
            console.error('Error obteniendo pedidos desde API para impresión:', err);
            alert('❌ Error al obtener pedidos para imprimir: ' + (err.message || err));
            return;
        }

        pedidosBeforeSedeFilter = pedidosToUse.length;

        // NO aplicar filtro de sede cuando el scope es 'selected'
        // porque el usuario ya seleccionó específicamente esos pedidos
        if (printChooserScope !== 'selected' && selectedSede && selectedSede !== 'all') {
            const sedeObj = sedes.find(s => s.id === String(selectedSede));
            if (sedeObj) {
                const target = normalize(sedeObj.direccion);
                const before = pedidosToUse.length;
                pedidosToUse = pedidosToUse.filter(p => normalize(p.ubicacion) === target);
                console.log(`🏢 Filtro de sede: ${before} → ${pedidosToUse.length} pedidos (sede: ${sedeObj.nombre})`);
            }
        }

        // dedupe and normalize
        const seen = new Set();
        pedidosToUse = (Array.isArray(pedidosToUse) ? pedidosToUse : []).filter(p => {
            const id = getPedidoId(p);
            if (!id) return false;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });

        // Diagnostic log for debugging
        try {
            const alm = getPedidosByType('almuerzo');
            const cen = getPedidosByType('cena');
            console.log('DEBUG handleConfirmPrintAction', {
                action,
                scope: printChooserScope,
                types: printChooserTypes,
                selectedPedidos,
                pedidosToUseCount: pedidosToUse.length,
                pedidosToUseIds: pedidosToUse.map(p => getPedidoId(p)),
                pedidosAlmuerzoIds: alm.map(p => getPedidoId(p)),
                pedidosCenaIds: cen.map(p => getPedidoId(p)),
                totalPedidosIds: pedidos.map(p => getPedidoId(p)),
                etiquetaConfigPresent: !!etiquetaConfig,
                selectedSede
            });
        } catch (e) {
            // ignore
        }

        if (!pedidosToUse || pedidosToUse.length === 0) {
            let errorMsg = '❌ No hay pedidos para imprimir.\n\n';

            if (printChooserScope === 'selected') {
                errorMsg += `Se seleccionaron ${selectedPedidos.length} pedido(s) pero la API no retornó datos.\n\n`;
                errorMsg += 'Posibles causas:\n';
                errorMsg += '• Los pedidos fueron eliminados de la base de datos\n';
                errorMsg += '• Hay un problema de conexión con el servidor\n';
                errorMsg += '• Los IDs de pedidos no son válidos';
            } else {
                if (pedidosBeforeSedeFilter > 0) {
                    errorMsg += `Se encontraron ${pedidosBeforeSedeFilter} pedido(s) pero fueron filtrados.\n\n`;
                    errorMsg += 'Posibles causas:\n';
                    errorMsg += '• Los pedidos no pertenecen a la sede seleccionada\n';
                    errorMsg += '• Verificá el filtro de sede en la parte superior';
                } else {
                    errorMsg += 'Posibles causas:\n';
                    errorMsg += '• No hay pedidos en la fecha/sede seleccionada\n';
                    errorMsg += '• Los pedidos no tienen el tipo de comida elegido (Almuerzo/Cena)\n';
                    errorMsg += '• Verificá los filtros aplicados';
                }
            }

            alert(errorMsg);
            return;
        }

        setPrintChooserOpen(false);
        if (action === 'print') {
            if (printMethod === 'qz') {
                await sendToQZ(pedidosToUse);
            } else if (printMethod === 'pdf') {
                generatePDF(pedidosToUse);
            } else if (printMethod === 'ip') {
                await printDirectToIP(pedidosToUse);
            } else if (printMethod === 'backend') {
                await printViaBackend(pedidosToUse);
            } else if (printMethod === 'raw') {
                downloadRawZPL(pedidosToUse);
            } else if (printMethod === 'usb') {
                await printViaUSB(pedidosToUse);
            }
        } else {
            if (printMethod === 'raw' || printMethod === 'usb') {
                downloadRawZPL(pedidosToUse);
            } else {
                generateZPL(pedidosToUse);
            }
        }
    };

    // --- Generación de ZPL usando la configuración ---
    // Ya no es necesario definir localmente generateZPLFromConfig ya que se importa de utilidades.

    const generateZPL = (pedidosAImprimir = pedidos) => {
        try {
            console.log('DEBUG generateZPL called for', pedidosAImprimir.length, 'pedidos', pedidosAImprimir.map(p => getPedidoId(p)));
        } catch (e) { }
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para generar ZPL.');
            return;
        }

        let zplCode = '';
        pedidosAImprimir.forEach(p => {
            zplCode += generateZPLFromConfig(p, etiquetaConfig);
        });

        const blob = new Blob([zplCode], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.zpl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        alert('✅ Archivo ZPL descargado.');
    };

    const sendToQZ = async (pedidosAImprimir = pedidos) => {
        try {
            console.log('DEBUG sendToQZ called for', pedidosAImprimir.length, 'pedidos', pedidosAImprimir.map(p => getPedidoId(p)));
        } catch (e) { }
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para imprimir.');
            return;
        }

        let zplCode = '';
        pedidosAImprimir.forEach(p => { zplCode += generateZPLFromConfig(p, etiquetaConfig); });

        // Diagnóstico detallado de QZ Tray
        console.log('DEBUG QZ Tray status:', {
            qzExists: !!window.qz,
            qzWebsocket: window.qz ? !!window.qz.websocket : false,
            qzActive: window.qz && window.qz.websocket ? window.qz.websocket.isActive() : false
        });

        // First try QZ Tray (if available)
        if (window.qz) {
            try {
                console.log('DEBUG: QZ Tray detectado, intentando conectar...');
                if (!window.qz.websocket.isActive()) {
                    console.log('DEBUG: Conectando a QZ Tray websocket...');
                    await window.qz.websocket.connect();
                    console.log('DEBUG: Conexión websocket establecida');
                }

                console.log('DEBUG: Buscando impresoras...');
                const printers = await window.qz.printers.find();
                console.log('DEBUG: Impresoras encontradas:', printers);

                let targetPrinter = printers.find(p =>
                    p.toLowerCase().includes('honeywell') ||
                    p.toLowerCase().includes('zebra') ||
                    p.toLowerCase().includes('zd421') ||
                    p.toLowerCase().includes('pc42') ||
                    p.toLowerCase().includes('pm43') ||
                    p.toLowerCase().includes('pm23')
                );

                if (!targetPrinter && printers.length > 0) {
                    targetPrinter = printers[0];
                    console.log('DEBUG: Usando primera impresora disponible:', targetPrinter);
                }

                if (!targetPrinter) throw new Error('No se detectaron impresoras en QZ Tray.');

                console.log('DEBUG: Configurando impresión para:', targetPrinter);
                const configQZ = window.qz.configs.create(targetPrinter);
                const data = [{ type: 'raw', format: 'plain', data: zplCode }];

                console.log('DEBUG: Enviando datos ZPL...');
                await window.qz.print(configQZ, data);

                alert('✅ Impresión enviada a ' + targetPrinter);
                return;
            } catch (err) {
                console.error('Error detallado imprimiendo con QZ Tray:', err);
                alert('❌ Error con QZ Tray: ' + err.message + '\n\nRevisá que QZ Tray esté ejecutándose y que hayas dado permisos al sitio.');
                // continue to try other methods
            }
        } else {
            console.log('DEBUG: window.qz no está disponible');
            alert('❌ QZ Tray no detectado\n\nAsegurate de que:\n1. QZ Tray esté ejecutándose\n2. Hayas aceptado el certificado en el navegador\n3. Hayas dado permisos al sitio');
        }

        // If QZ Tray not available or failed, try Honeywell Browser Print if present
        if (window.BrowserPrint) {
            try {
                // BrowserPrint may not be synchronous; try getDefaultDevice first
                const bp = window.BrowserPrint;
                const trySend = (device) => new Promise((resolve, reject) => {
                    try {
                        device.send(zplCode, () => {
                            resolve(true);
                        }, (err) => {
                            reject(err || new Error('Error enviando a BrowserPrint'));
                        });
                    } catch (e) {
                        reject(e);
                    }
                });

                // get default device
                const defaultDevice = await new Promise((resolve, reject) => {
                    try {
                        bp.getDefaultDevice('printer', (dev) => resolve(dev), (err) => reject(err));
                    } catch (e) { reject(e); }
                });

                if (defaultDevice) {
                    await trySend(defaultDevice);
                    alert('✅ Impresión enviada vía Browser Print a la impresora por defecto.');
                    return;
                }

                // fallback: list devices and use first
                const devices = await new Promise((resolve, reject) => {
                    try {
                        bp.getDeviceList('printer', (list) => resolve(list), (err) => reject(err));
                    } catch (e) { reject(e); }
                });
                if (devices && devices.length > 0) {
                    await trySend(devices[0]);
                    alert('✅ Impresión enviada vía Browser Print a ' + (devices[0].name || 'impresora'));
                    return;
                }
            } catch (err) {
                console.error('Error imprimiendo con Browser Print:', err);
                // continue to fallback download
            }
        }

        // fallback: descargar ZPL para impresión manual/desde otro equipo
        console.log('DEBUG sendToQZ: no hay método de impresión disponible, falling back to download');
        const blob = new Blob([zplCode], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.zpl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        alert('No se detectó QZ Tray ni Browser Print: se ha descargado el archivo .zpl');
    };

    // Función para generar PDF de etiquetas
    const generatePDF = (pedidosAImprimir = pedidos) => {
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para generar PDF.');
            return;
        }

        const htmlContent = generatePDFHTML(pedidosAImprimir, etiquetaConfig);

        // Crear ventana para imprimir con configuración optimizada
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();

        console.log('✅ PDF generado con configuración de etiquetas personalizada');
        alert(`✅ Se abrió ventana para imprimir ${pedidosAImprimir.length} etiqueta(s).\n\n💡 Configurá tu impresora:\n- Tamaño: A4\n- Orientación: Vertical\n- Márgenes: Mínimos\n- Escala: 100%`);
    };

    // Función para imprimir directamente por IP (puerto 9100)
    // Función para imprimir directamente por IP (puerto 9100) con ZPL crudo
    const printDirectToIP = async (pedidosAImprimir = pedidos) => {
        if (!printerIP.trim()) {
            alert('⚠️ Ingresa la IP de la impresora (ej: 192.168.1.100)');
            return;
        }

        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para imprimir.');
            return;
        }

        console.log('Generando ZPL crudo para impresión directa...');
        const zplCode = generateRawZPL(pedidosAImprimir, etiquetaConfig);

        if (!zplCode.trim()) {
            alert('❌ Error al generar código ZPL');
            return;
        }

        console.log('ZPL generado:', zplCode.substring(0, 200) + '...');

        try {
            const response = await fetch('/api/print-direct', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    printerIP: printerIP.trim(),
                    zplData: zplCode
                })
            });

            if (response.ok) {
                alert(`✅ ${pedidosAImprimir.length} etiqueta(s) enviada(s) en modo RAW a la impresora ${printerIP}\n\n💡 La impresora debería imprimir directamente sin drivers.`);
            } else {
                const errorText = await response.text();
                throw new Error('Error en el servidor: ' + errorText);
            }
        } catch (error) {
            console.error('Error imprimiendo por IP:', error);
            alert(`❌ Error al imprimir por IP: ${error.message}\n\nVerificá que:\n1. La IP sea correcta (${printerIP})\n2. La impresora esté encendida y en red\n3. El puerto 9100 esté abierto\n4. La impresora acepte comandos ZPL RAW`);
        }
    };

    // Función para descargar ZPL crudo
    const downloadRawZPL = (pedidosAImprimir = pedidos) => {
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para generar ZPL.');
            return;
        }

        console.log('Generando ZPL crudo para descarga...');
        const zplCode = generateRawZPL(pedidosAImprimir, etiquetaConfig);

        if (!zplCode.trim()) {
            alert('❌ Error al generar código ZPL');
            return;
        }

        // Descargar archivo ZPL
        const blob = new Blob([zplCode], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquetas_raw_${new Date().toISOString().split('T')[0]}.zpl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        alert(`✅ Archivo ZPL crudo descargado con ${pedidosAImprimir.length} etiqueta(s)\n\n💡 Podés enviarlo directamente a la impresora usando:\n- Comandos de terminal (netcat, telnet)\n- Software de impresión ZPL\n- Copiar y pegar en puerto RAW`);
    };

    // Función para imprimir por USB usando CUPS
    const printViaUSB = async (pedidosAImprimir = pedidos) => {
        if (!selectedUSBPrinter.trim()) {
            alert('⚠️ Seleccioná una impresora USB de la lista');
            return;
        }

        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para imprimir.');
            return;
        }

        console.log('Generando ZPL crudo para impresión USB...');
        const zplCode = generateRawZPL(pedidosAImprimir, etiquetaConfig);

        if (!zplCode.trim()) {
            alert('❌ Error al generar código ZPL');
            return;
        }

        console.log(`Enviando ${pedidosAImprimir.length} etiqueta(s) a ${selectedUSBPrinter}...`);

        try {
            const response = await fetch('http://localhost:8085/api/print-usb', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    printerName: selectedUSBPrinter.trim(),
                    zplData: zplCode
                })
            });

            if (response.ok) {
                const data = await response.json();
                alert(`✅ ${pedidosAImprimir.length} etiqueta(s) enviada(s) a ${selectedUSBPrinter}\n\n💡 La impresora debería imprimir en unos segundos.\n\n${data.jobInfo || ''}`);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.details || response.statusText);
            }
        } catch (error) {
            console.error('Error imprimiendo por USB:', error);
            alert(`❌ Error al imprimir por USB: ${error.message}\n\nVerificá que:\n1. La impresora esté conectada y encendida\n2. El backend esté corriendo (puerto 8085)\n3. La impresora esté configurada en CUPS\n4. Tengas permisos para imprimir`);
        }
    };

    // Función para imprimir vía backend
    const printViaBackend = async (pedidosAImprimir = pedidos) => {
        if (!pedidosAImprimir || pedidosAImprimir.length === 0) {
            alert('No hay pedidos para imprimir.');
            return;
        }

        let zplCode = '';
        pedidosAImprimir.forEach(p => { zplCode += generateZPLFromConfig(p, etiquetaConfig); });

        try {
            const response = await fetch('/api/print-backend', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    zplData: zplCode,
                    pedidos: pedidosAImprimir.map(p => ({
                        id: getPedidoId(p),
                        nombre: p.nombreuser,
                        apellido: p.apellido,
                        plato: p.nombre
                    }))
                })
            });

            if (response.ok) {
                alert('✅ Etiquetas enviadas al servidor para imprimir');
            } else {
                throw new Error('Error en el servidor: ' + response.statusText);
            }
        } catch (error) {
            console.error('Error imprimiendo vía backend:', error);
            alert('❌ Error al imprimir vía backend: ' + error.message);
        }

        try {
            if (!window.qz.websocket.isActive()) {
                await window.qz.websocket.connect();
            }
            const printers = await window.qz.printers.find();
            let zebraPrinter = printers.find(p => p.toLowerCase().includes('zd421') || p.toLowerCase().includes('zebra'));
            if (!zebraPrinter && printers.length > 0) zebraPrinter = printers[0];
            if (!zebraPrinter) throw new Error('No se detectaron impresoras.');
            const configQZ = window.qz.configs.create(zebraPrinter);
            const data = [{ type: 'raw', format: 'plain', data: zplCode }];
            await window.qz.print(configQZ, data);
            alert('✅ Impresión enviada a ' + zebraPrinter);
        } catch (err) {
            console.error('Error imprimiendo con QZ Tray:', err);
            alert('Error al imprimir con QZ Tray: ' + (err.message || err));
            // fallback: descargar
            const blob = new Blob([zplCode], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `etiquetas_zebra_${new Date().toISOString().split('T')[0]}.zpl`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
    };

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-b-4 border-indigo-500">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-4 rounded-xl shadow-lg">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-gray-800">Administración de Pedidos</h2>
                                <p className="text-gray-600">Gestión, impresión y exportación de etiquetas</p>
                            </div>
                        </div>

                        {/* Filtros */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Selector de empresa */}
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    Empresa
                                </label>
                                <select
                                    className="px-4 py-3 rounded-xl bg-white text-gray-800 border-2 border-indigo-300 font-semibold focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 transition shadow-md"
                                    value={selectedEmpresa}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedEmpresa(val);
                                        try { localStorage.setItem('selectedEmpresaAbmPedidos', val); } catch (e) { }
                                        setSelectedSede('all');
                                        localStorage.setItem('selectedSedeAbmPedidos', 'all');
                                        setSelectedPedidos([]);
                                    }}
                                >
                                    <option value="all">🌐 Todas las empresas</option>
                                    {empresas.map(emp => (
                                        <option key={emp.id_empresa} value={emp.id_empresa}>{emp.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Selector de sede */}
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Sede
                                </label>
                                <select
                                    className="px-4 py-3 rounded-xl bg-white text-gray-800 border-2 border-purple-300 font-semibold focus:ring-2 focus:ring-purple-200 focus:border-purple-500 transition shadow-md"
                                    value={selectedSede}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedSede(val);
                                        try { localStorage.setItem('selectedSedeAbmPedidos', val); } catch (e) { }
                                        setSelectedPedidos([]);
                                    }}
                                >
                                    <option value="all">📍 Todas las sedes</option>
                                    {sedes.map(s => (
                                        <option key={s.id} value={s.id}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Botonera de acciones */}
                        <div className="flex flex-wrap gap-3 mt-6 justify-end">
                            <button
                                onClick={() => setShowConfigurador(true)}
                                className="px-5 py-3 rounded-xl text-white font-bold bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transition-all shadow-lg flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Configurar Etiquetas
                            </button>

                            <button
                                onClick={() => {
                                    if (selectedPedidos && selectedPedidos.length > 0) {
                                        const selectedSet = new Set(selectedPedidos);
                                        const alm = getPedidosByType('almuerzo').some(p => selectedSet.has(getPedidoId(p)));
                                        const cen = getPedidosByType('cena').some(p => selectedSet.has(getPedidoId(p)));
                                        setPrintChooserTypes({ almuerzo: alm, cena: cen });
                                        setPrintChooserScope('selected');
                                    } else {
                                        setPrintChooserTypes({ almuerzo: true, cena: true });
                                        setPrintChooserScope('all');
                                    }
                                    setPrintChooserOpen(true);
                                }}
                                className="px-5 py-3 rounded-xl text-white font-bold bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 transition-all shadow-lg flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                Imprimir/Descargar
                            </button>

                            <button
                                onClick={handleSelectAll}
                                className="px-5 py-3 rounded-xl text-gray-800 font-bold bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 transition-all shadow-md"
                            >
                                {selectedPedidos.length === pedidos.length ? '❌ Deseleccionar todo' : '✅ Seleccionar todo'}
                            </button>

                            <button
                                onClick={exportToExcel}
                                className="px-5 py-3 rounded-xl text-white font-bold bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 transition-all shadow-lg flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Exportar Excel
                            </button>
                        </div>
                    </div>

                    {/* Modal: selector de impresión/descarga por tipo */}
                    {printChooserOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border-4 border-indigo-500 max-h-[90vh] overflow-y-auto">
                                <div className="sticky top-0 bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-white">🖨️ Imprimir / Descargar etiquetas</h3>
                                    <button onClick={() => setPrintChooserOpen(false)} className="text-white hover:text-gray-200 text-2xl font-bold">✕</button>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="mb-3">
                                        <label className="flex items-center gap-2"><input type="checkbox" checked={printChooserTypes.almuerzo} onChange={(e) => setPrintChooserTypes(prev => ({ ...prev, almuerzo: e.target.checked }))} /> Almuerzo</label>
                                        <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={printChooserTypes.cena} onChange={(e) => setPrintChooserTypes(prev => ({ ...prev, cena: e.target.checked }))} /> Cena</label>
                                    </div>
                                    <div className="mb-3">
                                        <label className="flex items-center gap-2"><input type="radio" name="scope" checked={printChooserScope === 'all'} onChange={() => setPrintChooserScope('all')} /> Todas</label>
                                        <label className="flex items-center gap-2 mt-2"><input type="radio" name="scope" checked={printChooserScope === 'selected'} onChange={() => setPrintChooserScope('selected')} /> Solo seleccionadas</label>
                                    </div>
                                    <div className="mb-3">
                                        <h4 className="text-sm font-semibold mb-2">Método de Impresión:</h4>
                                        <label className="flex items-center gap-2"><input type="radio" name="printMethod" checked={printMethod === 'usb'} onChange={() => setPrintMethod('usb')} /> 🖨️ USB/CUPS (Recomendado)</label>
                                        <label className="flex items-center gap-2 mt-1"><input type="radio" name="printMethod" checked={printMethod === 'raw'} onChange={() => setPrintMethod('raw')} /> 🔧 ZPL Crudo (Descargar)</label>
                                        <label className="flex items-center gap-2 mt-1"><input type="radio" name="printMethod" checked={printMethod === 'pdf'} onChange={() => setPrintMethod('pdf')} /> 📄 PDF</label>
                                        <label className="flex items-center gap-2 mt-1"><input type="radio" name="printMethod" checked={printMethod === 'qz'} onChange={() => setPrintMethod('qz')} /> ⚙️ QZ Tray</label>
                                        <label className="flex items-center gap-2 mt-1"><input type="radio" name="printMethod" checked={printMethod === 'ip'} onChange={() => setPrintMethod('ip')} /> 🌐 IP Directa (RAW)</label>
                                        <label className="flex items-center gap-2 mt-1"><input type="radio" name="printMethod" checked={printMethod === 'backend'} onChange={() => setPrintMethod('backend')} /> 🖥️ Servidor</label>
                                    </div>
                                    {printMethod === 'usb' && (
                                        <div className="mb-3">
                                            <label className="block text-sm font-medium mb-1">Impresora USB:</label>
                                            <select
                                                value={selectedUSBPrinter}
                                                onChange={(e) => setSelectedUSBPrinter(e.target.value)}
                                                className="w-full px-3 py-1 bg-slate-800 border border-slate-600 rounded text-white"
                                                disabled={loadingPrinters}
                                            >
                                                <option value="">-- Seleccionar impresora --</option>
                                                {usbPrinters.filter(p => p.isUSB || p.name.toLowerCase().includes('honeywell')).map((printer, idx) => (
                                                    <option key={idx} value={printer.name}>
                                                        {printer.name} {printer.status === 'disabled' ? '(deshabilitada)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {loadingPrinters && <div className="text-xs text-slate-400 mt-1">Cargando impresoras...</div>}
                                            {!loadingPrinters && usbPrinters.length === 0 && (
                                                <div className="text-xs text-yellow-300 mt-1">No se encontraron impresoras USB. Verificá CUPS.</div>
                                            )}
                                            <button
                                                onClick={loadUSBPrinters}
                                                className="mt-1 text-xs text-blue-300 hover:text-blue-200"
                                            >
                                                🔄 Recargar lista
                                            </button>
                                        </div>
                                    )}
                                    {printMethod === 'ip' && (
                                        <div className="mb-3">
                                            <label className="block text-sm font-medium mb-1">IP de la Impresora:</label>
                                            <input
                                                type="text"
                                                value={printerIP}
                                                onChange={(e) => setPrinterIP(e.target.value)}
                                                placeholder="ej: 192.168.1.100"
                                                className="w-full px-3 py-1 bg-slate-800 border border-slate-600 rounded text-white"
                                            />
                                            <button
                                                onClick={scanForHoneywellIP}
                                                className="mt-1 text-xs text-blue-300 hover:text-blue-200"
                                            >
                                                🔍 Buscar automáticamente
                                            </button>
                                        </div>
                                    )}

                                    {/* Sección de Diagnósticos */}
                                    <div className="mb-3 border-t border-slate-700 pt-3">
                                        <button
                                            onClick={() => setShowDiagnostics(!showDiagnostics)}
                                            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"
                                        >
                                            {showDiagnostics ? '▼' : '▶'} Diagnósticos de Impresión
                                        </button>

                                        {showDiagnostics && (
                                            <div className="mt-2 space-y-2 text-xs">
                                                {/* Estado QZ Tray */}
                                                <div className="bg-slate-800 p-2 rounded">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">QZ Tray:</span>
                                                        {printDiagnostics.qzStatus === 'testing' && <span className="text-yellow-300">🔄 Verificando...</span>}
                                                        {printDiagnostics.qzStatus === 'connected' && <span className="text-green-300">✅ Conectado</span>}
                                                        {printDiagnostics.qzStatus === 'failed' && <span className="text-red-300">❌ Error</span>}
                                                        {printDiagnostics.qzStatus === 'not_loaded' && <span className="text-orange-300">⚠️ No cargado</span>}
                                                        {printDiagnostics.qzStatus === 'unknown' && <span className="text-gray-300">❓ Desconocido</span>}
                                                    </div>
                                                    <button
                                                        onClick={testQZConnection}
                                                        className="mt-1 text-blue-300 hover:text-blue-200"
                                                    >
                                                        🔄 Volver a probar
                                                    </button>
                                                </div>

                                                {/* Información del sistema */}
                                                {printDiagnostics.systemInfo && (
                                                    <div className="bg-slate-800 p-2 rounded">
                                                        <div className="font-medium mb-1">Información del sistema:</div>
                                                        <div>QZ Versión: {printDiagnostics.systemInfo.version}</div>
                                                        <div>Impresoras totales: {printDiagnostics.systemInfo.totalPrinters}</div>
                                                        {printDiagnostics.systemInfo.honeywellPrinters.length > 0 && (
                                                            <div className="text-green-300">
                                                                ✅ Honeywell detectadas: {printDiagnostics.systemInfo.honeywellPrinters.join(', ')}
                                                            </div>
                                                        )}
                                                        {printDiagnostics.systemInfo.honeywellPrinters.length === 0 && (
                                                            <div className="text-yellow-300">
                                                                ⚠️ No se detectaron impresoras Honeywell
                                                            </div>
                                                        )}
                                                        <details className="mt-1">
                                                            <summary className="cursor-pointer text-blue-300">Ver todas las impresoras</summary>
                                                            <div className="mt-1 max-h-20 overflow-y-auto">
                                                                {printDiagnostics.systemInfo.printers.map((printer, idx) => (
                                                                    <div key={idx} className="text-xs text-slate-300">• {printer}</div>
                                                                ))}
                                                            </div>
                                                        </details>
                                                    </div>
                                                )}

                                                {/* Recomendaciones específicas por método */}
                                                <div className="bg-blue-900/30 p-2 rounded text-blue-200">
                                                    <div className="font-medium mb-1">💡 Recomendaciones para tu método:</div>
                                                    {printMethod === 'raw' && (
                                                        <div className="text-xs">
                                                            <strong>ZPL Crudo:</strong><br />
                                                            • ✅ Ideal para impresoras Honeywell en modo RAW<br />
                                                            • Descarga archivo .zpl para envío directo<br />
                                                            • Usa con terminal: `cat archivo.zpl | nc IP_IMPRESORA 9100`
                                                        </div>
                                                    )}
                                                    {printMethod === 'ip' && (
                                                        <div className="text-xs">
                                                            <strong>IP Directa:</strong><br />
                                                            • Envía ZPL crudo directo al puerto 9100<br />
                                                            • Verificá IP de red de la impresora<br />
                                                            • No requiere drivers instalados
                                                        </div>
                                                    )}
                                                    {printMethod === 'pdf' && (
                                                        <div className="text-xs">
                                                            <strong>PDF:</strong><br />
                                                            • Funciona con cualquier impresora<br />
                                                            • Requiere drivers normales instalados<br />
                                                            • Abre ventana de impresión del navegador
                                                        </div>
                                                    )}
                                                    {printMethod === 'qz' && (
                                                        <div className="text-xs">
                                                            <strong>QZ Tray:</strong><br />
                                                            • Requiere QZ Tray ejecutándose<br />
                                                            • Certificados de seguridad configurados<br />
                                                            • Compatible con impresoras ZPL/EPL
                                                        </div>
                                                    )}
                                                    <div className="text-xs mt-1 border-t border-blue-700 pt-1">
                                                        <strong>Para Honeywell:</strong> Usá "ZPL Crudo" o "IP Directa"
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {(() => {
                                        const hasSelected = selectedPedidos && selectedPedidos.length > 0;
                                        const anyTypeSelected = printChooserTypes.almuerzo || printChooserTypes.cena;
                                        const disableActions = (printChooserScope === 'selected' && !hasSelected) || !anyTypeSelected;
                                        return (
                                            <>
                                                {!anyTypeSelected && (
                                                    <div className="mb-2 p-2 text-sm text-yellow-300 bg-yellow-900/30 rounded border border-yellow-700">
                                                        ⚠️ Seleccioná al menos un tipo (Almuerzo/Cena) para continuar.
                                                    </div>
                                                )}
                                                {printChooserScope === 'selected' && !hasSelected && (
                                                    <div className="mb-2 p-2 text-sm text-yellow-300 bg-yellow-900/30 rounded border border-yellow-700">
                                                        ⚠️ No hay pedidos seleccionados.
                                                        <br />
                                                        <span className="text-xs">Seleccioná filas marcando los checkboxes o cambiá a "Todas".</span>
                                                    </div>
                                                )}
                                                {printChooserScope === 'selected' && hasSelected && (
                                                    <div className="mb-2 p-2 text-sm text-green-300 bg-green-900/30 rounded border border-green-700">
                                                        ✅ {selectedPedidos.length} pedido(s) seleccionado(s)
                                                    </div>
                                                )}
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => setPrintChooserOpen(false)} className="px-3 py-2 rounded bg-slate-700">Cancelar</button>
                                                    <button disabled={disableActions} onClick={() => handleConfirmPrintAction({ action: 'download' })} className={`px-3 py-2 rounded bg-purple-600 ${disableActions ? 'opacity-50 cursor-not-allowed' : ''}`}>💾 Descargar ZPL</button>
                                                    <button disabled={disableActions} onClick={() => handleConfirmPrintAction({ action: 'print' })} className={`px-3 py-2 rounded bg-fuchsia-600 ${disableActions ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                        {printMethod === 'usb' ? '🖨️ Imprimir USB' :
                                                            printMethod === 'pdf' ? '📄 Imprimir PDF' :
                                                                printMethod === 'qz' ? '⚙️ QZ Tray' :
                                                                    printMethod === 'ip' ? '🌐 IP Directa' :
                                                                        printMethod === 'backend' ? '🖥️ Servidor' :
                                                                            '🖨️ Imprimir'}
                                                    </button>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}

                    {(() => {
                        const normalizeInner = (t) => (t || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
                        const base = getFilteredPedidos();
                        let pedidosAlmuerzo = base.filter(p => normalizeInner(p.comida) === 'almuerzo');
                        let pedidosCena = base.filter(p => normalizeInner(p.comida) === 'cena');
                        if (base.length > 0 && pedidosAlmuerzo.length === 0 && pedidosCena.length === 0) {
                            pedidosAlmuerzo = base.filter(p => normalizeInner(p.tipo) === 'almuerzo');
                            pedidosCena = base.filter(p => normalizeInner(p.tipo) === 'cena');
                        }

                        return (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* ALMUERZO */}
                                <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-orange-200">
                                    <div
                                        className="relative rounded-xl overflow-hidden mb-4 shadow-lg"
                                        style={{ backgroundImage: `url(${foodImage})`, backgroundSize: 'cover', backgroundPosition: 'center', height: '120px' }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/60 to-red-600/60" />
                                        <div className="relative z-10 flex items-center justify-between px-5 py-4 h-full">
                                            <div>
                                                <h3 className="text-2xl font-bold text-white drop-shadow-lg">🌅 Almuerzo</h3>
                                                <p className="text-white/90 font-semibold text-sm mt-1">Total: {pedidosAlmuerzo.length} pedidos</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="rounded-xl border-2 border-orange-200 overflow-hidden shadow-lg max-h-[600px] overflow-y-auto">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-gradient-to-r from-orange-500 to-red-600 text-white sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-3 py-3 text-left">
                                                        <input type="checkbox" checked={isAllSelectedInGroup('almuerzo')} onChange={() => handleSelectAllGroup('almuerzo')} className="rounded" />
                                                    </th>
                                                    <th className="px-3 py-3 text-left font-bold">Plato</th>
                                                    <th className="px-3 py-3 text-left font-bold">Bebida</th>
                                                    <th className="px-3 py-3 text-left font-bold">Fecha</th>
                                                    <th className="px-3 py-3 text-left font-bold">Nombre</th>
                                                    <th className="px-3 py-3 text-left font-bold">Apellido</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-orange-100">
                                                {pedidosAlmuerzo.map((order) => {
                                                    const bebida = order.bebida_id ? bebidas.find(b => b.id_bebida === order.bebida_id) : null;
                                                    const nombrePlato = order.plato?.nombre || order.nombre || (order.bebida_id ? '(Solo bebida)' : 'Sin plato');
                                                    return (
                                                        <tr key={`alm-${getPedidoId(order)}`} className="hover:bg-orange-50 transition-colors">
                                                            <td className="px-3 py-3">
                                                                <input type="checkbox" checked={selectedPedidos.includes(getPedidoId(order))} onChange={() => handleSelectPedido(getPedidoId(order))} className="rounded" />
                                                            </td>
                                                            <td className="px-3 py-3 text-gray-800 font-semibold">{nombrePlato}</td>
                                                            <td className="px-3 py-3 text-emerald-600 font-medium">
                                                                {bebida ? (
                                                                    <span className="text-xs">{bebida.tipo === 'gaseosa' ? '🥤' : '💧'} {bebida.marca} - {bebida.sabor}</span>
                                                                ) : (
                                                                    <span className="text-gray-400 text-xs italic">Sin bebida</span>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-3 text-gray-700">{order.fecha}</td>
                                                            <td className="px-3 py-3 text-gray-700">{order.nombreuser}</td>
                                                            <td className="px-3 py-3 text-gray-700">{order.apellido}</td>
                                                        </tr>
                                                    );
                                                })}
                                                {pedidosAlmuerzo.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500 italic">No hay pedidos de almuerzo.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* CENA */}
                                <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-purple-200">
                                    <div
                                        className="relative rounded-xl overflow-hidden mb-4 shadow-lg"
                                        style={{ backgroundImage: `url(${dinnerImage})`, backgroundSize: 'cover', backgroundPosition: 'center', height: '120px' }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/60 to-indigo-600/60" />
                                        <div className="relative z-10 flex items-center justify-between px-5 py-4 h-full">
                                            <div>
                                                <h3 className="text-2xl font-bold text-white drop-shadow-lg">🌙 Cena</h3>
                                                <p className="text-white/90 font-semibold text-sm mt-1">Total: {pedidosCena.length} pedidos</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="rounded-xl border-2 border-purple-200 overflow-hidden shadow-lg max-h-[600px] overflow-y-auto">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-3 py-3 text-left">
                                                        <input type="checkbox" checked={isAllSelectedInGroup('cena')} onChange={() => handleSelectAllGroup('cena')} className="rounded" />
                                                    </th>
                                                    <th className="px-3 py-3 text-left font-bold">Plato</th>
                                                    <th className="px-3 py-3 text-left font-bold">Bebida</th>
                                                    <th className="px-3 py-3 text-left font-bold">Fecha</th>
                                                    <th className="px-3 py-3 text-left font-bold">Nombre</th>
                                                    <th className="px-3 py-3 text-left font-bold">Apellido</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-purple-100">
                                                {pedidosCena.map((order) => {
                                                    const bebida = order.bebida_id ? bebidas.find(b => b.id_bebida === order.bebida_id) : null;
                                                    const nombrePlato = order.plato?.nombre || order.nombre || (order.bebida_id ? '(Solo bebida)' : 'Sin plato');
                                                    return (
                                                        <tr key={`cena-${getPedidoId(order)}`} className="hover:bg-purple-50 transition-colors">
                                                            <td className="px-3 py-3">
                                                                <input type="checkbox" checked={selectedPedidos.includes(getPedidoId(order))} onChange={() => handleSelectPedido(getPedidoId(order))} className="rounded" />
                                                            </td>
                                                            <td className="px-3 py-3 text-gray-800 font-semibold">{nombrePlato}</td>
                                                            <td className="px-3 py-3 text-emerald-600 font-medium">
                                                                {bebida ? (
                                                                    <span className="text-xs">{bebida.tipo === 'gaseosa' ? '🥤' : '💧'} {bebida.marca} - {bebida.sabor}</span>
                                                                ) : (
                                                                    <span className="text-gray-400 text-xs italic">Sin bebida</span>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-3 text-gray-700">{order.fecha}</td>
                                                            <td className="px-3 py-3 text-gray-700">{order.nombreuser}</td>
                                                            <td className="px-3 py-3 text-gray-700">{order.apellido}</td>
                                                        </tr>
                                                    );
                                                })}
                                                {pedidosCena.length === 0 && (
                                                    <tr>
                                                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500 italic">No hay pedidos de cena.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Modal configurador de etiquetas */}
            <ConfiguradorEtiquetas
                isOpen={showConfigurador}
                onClose={() => setShowConfigurador(false)}
                onSaveConfig={(config) => { setEtiquetaConfig(config); setShowConfigurador(false); }}
                pedidosReales={getFilteredPedidos()}
            />
        </>
    );
}

export default AbmPedidos;

